import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Sparkles, Quote, Star, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SharedReadingPage = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/social/shared/${shareId}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 410 ? "This shared reading has expired." : "Reading not found.");
      } finally {
        setLoading(false);
      }
    };
    fetchShared();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#070B14" }}>
        <div className="text-center space-y-4">
          <p className="text-white/60 font-body text-lg">{error}</p>
          <Button onClick={() => navigate("/")} className="btn-gold">
            <Sparkles className="w-4 h-4 mr-2" /> Get Your Own Guidance
          </Button>
        </div>
      </div>
    );
  }

  const { share_type, content, user_name } = data;

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="shared-reading-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 space-y-8">
        {/* Header */}
        <div className="text-center space-y-3 animate-fade-in-up">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B5CF6] flex items-center justify-center mx-auto">
            {share_type === "tarot" ? <Star className="w-7 h-7 text-white" /> : <Sparkles className="w-7 h-7 text-white" />}
          </div>
          <p className="text-[#D4AF37] text-sm tracking-wider uppercase font-body">Shared by {user_name}</p>
          <h1 className="font-display text-3xl text-white">
            {share_type === "tarot" ? "Tarot Reading" : share_type === "wisdom" ? "Daily Wisdom" : "Daily Guidance"}
          </h1>
        </div>

        {/* Content */}
        {share_type === "wisdom" && content?.wise_saying && (
          <div className="gold-accent-card p-8 text-center animate-fade-in-up">
            <Quote className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
            <p className="font-display text-2xl text-white italic leading-relaxed">"{content.wise_saying}"</p>
          </div>
        )}

        {share_type === "guidance" && (
          <div className="space-y-4 animate-fade-in-up">
            {content?.headline && (
              <div className="gold-accent-card p-6">
                <h2 className="font-display text-2xl text-white mb-3">{content.headline}</h2>
                {content.overview && <p className="text-white/70 font-body">{content.overview}</p>}
              </div>
            )}
            {content?.encouragement && (
              <div className="glass-card p-6">
                <p className="text-white/80 font-body italic">{content.encouragement}</p>
              </div>
            )}
          </div>
        )}

        {share_type === "tarot" && content && (
          <div className="space-y-4 animate-fade-in-up">
            {content.overall_theme && (
              <div className="gold-accent-card p-6">
                <h2 className="font-display text-xl text-white">{content.overall_theme}</h2>
              </div>
            )}
            {content.affirmation && (
              <div className="glass-card p-6 text-center">
                <p className="font-display text-lg text-white italic">"{content.affirmation}"</p>
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <div className="text-center space-y-4 pt-4 animate-fade-in-up">
          <p className="text-white/40 font-body text-sm">Get your own personalized daily guidance</p>
          <Button onClick={() => navigate("/")} className="btn-gold px-8 py-3 text-lg" data-testid="shared-cta-btn">
            Begin Your Journey <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-white/20 text-xs font-body">Powered by SoulGuide</p>
        </div>
      </div>
    </div>
  );
};

export default SharedReadingPage;
